/**
 * Master-type page (Event catalog) — shared by Entity Types and Actions.
 *
 * Entity types (nouns) and actions (verbs) have the same shape (name, description,
 * status), so one component drives both pages via `config`.
 *
 * Add / Edit are FULL-PAGE views (list/form toggle), styled like the other pages
 * (Back button + Cards) — no modal popups. Archive uses a small confirm dialog, matching
 * the app's existing delete-confirm pattern.
 *
 * List interactivity is kept in component-local state seeded from the GET mock, since the
 * mocks are static until the backend ships.
 *
 * NOTE: viewer-only button hiding (spec §2) can't gate on a role yet — v1.0.36 removed roles.
 * `canManage` is a placeholder (TODO: bind to RBAC) and is true for now.
 */

import * as React from 'react'
import { useApiQuery, useApiMutation } from '@/api/ApiQueryServiceProvider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { useToast } from '@/contexts/ToastContext'
import { AppLogger } from '@/lib/logger'
import { Search, Edit, Trash2, Plus, Loader2, ArrowLeft, type LucideIcon } from 'lucide-react'

const log = AppLogger.getLogger(import.meta)

export interface MasterTypeConfig {
  title: string
  singular: string
  icon: LucideIcon
  listEndpoint: string
  upsertEndpoint: string
  archiveEndpoint: string
  dataKey: string
  numberKey: string
  nameKey: string
  descKey: string
  nameRegex: RegExp
  nameHint: string
  nameErrorHint: string
  namePlaceholder: string
}

interface Row {
  id: string
  number: number
  name: string
  description: string
  status: string
  archived: boolean // derived from archiveDate — not a status value
}

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)
const ITEMS_PER_PAGE = 10
type View = 'list' | 'form'

export function MasterTypeList({ config }: { config: MasterTypeConfig }) {
  const { toast } = useToast()
  const { data, isLoading, isError, refetch } = useApiQuery(config.listEndpoint, {}, {})

  const toRow = (r: any): Row => ({
    id: String(r[config.numberKey]),
    number: r[config.numberKey],
    name: r[config.nameKey],
    description: r[config.descKey] ?? '',
    status: r.status ?? 'active',
    archived: !!r.archiveDate,
  })
  const list: Row[] = (((data as any)?.content?.[config.dataKey] ?? []) as any[]).map(toRow)

  // Mutations invalidate the list (and event types, which join entity/action names) so the
  // table refetches from the backend after a create / edit / archive.
  const invalidates = [config.listEndpoint, 'getEventTypes']
  const upsert = useApiMutation(config.upsertEndpoint, 'POST', { invalidates })
  const archive = useApiMutation(config.archiveEndpoint, 'DELETE', { invalidates })

  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('all')
  const [page, setPage] = React.useState(1)

  // Full-page form view (no modal for add/edit)
  const [view, setView] = React.useState<View>('list')
  const [formMode, setFormMode] = React.useState<'add' | 'edit'>('add')
  const [editingRow, setEditingRow] = React.useState<Row | null>(null)
  const [archiving, setArchiving] = React.useState<Row | null>(null)

  const [fName, setFName] = React.useState('')
  const [fDesc, setFDesc] = React.useState('')
  const [fStatus, setFStatus] = React.useState('active')
  const [submitted, setSubmitted] = React.useState(false)

  const canManage = true // TODO: gate on RBAC role when available
  const busy = upsert.isPending || archive.isPending

  // Live, specific name validation (shown inline). Name is locked in edit mode.
  const computeNameError = (name: string, didSubmit: boolean): string | null => {
    if (formMode === 'edit') return null
    const n = name.trim()
    if (!n) return didSubmit ? 'Name is required' : null
    if (/\s/.test(name)) return 'No spaces allowed'
    if (!config.nameRegex.test(n)) return config.nameErrorHint
    if (list.some((r) => r.name === n)) return `A ${config.singular} named "${n}" already exists.`
    return null
  }
  const nameError = computeNameError(fName, submitted)

  const filtered = list.filter((r) => {
    const q = search.trim().toLowerCase()
    const matchesSearch = !q || r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q)
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter
    return matchesSearch && matchesStatus
  }).sort((a, b) => a.name.localeCompare(b.name))

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
  React.useEffect(() => { setPage(1) }, [search, statusFilter])

  const openAdd = () => { setFormMode('add'); setEditingRow(null); setFName(''); setFDesc(''); setFStatus('active'); setSubmitted(false); setView('form') }
  const openEdit = (r: Row) => { setFormMode('edit'); setEditingRow(r); setFName(r.name); setFDesc(r.description); setFStatus(r.status); setSubmitted(false); setView('form') }
  const backToList = () => { setView('list'); setEditingRow(null) }

  const handleSave = async () => {
    setSubmitted(true)
    if (formMode === 'add') {
      if (computeNameError(fName, true)) return
      try {
        await upsert.mutateAsync({ [config.nameKey]: fName.trim(), [config.descKey]: fDesc, status: fStatus })
        toast({ title: `${cap(config.singular)} added`, description: `${fName.trim()} created.`, type: 'success' })
        backToList()
      } catch (err) {
        log.error(`Failed to create ${config.singular}`, err)
        toast({ title: `Could not add ${config.singular}`, description: (err as Error)?.message || 'Please try again.', type: 'error' })
      }
    } else if (editingRow) {
      try {
        await upsert.mutateAsync({ [config.numberKey]: editingRow.number, [config.nameKey]: editingRow.name, [config.descKey]: fDesc, status: fStatus })
        toast({ title: `${cap(config.singular)} updated`, type: 'success' })
        backToList()
      } catch (err) {
        log.error(`Failed to update ${config.singular}`, err)
        toast({ title: `Could not update ${config.singular}`, description: (err as Error)?.message || 'Please try again.', type: 'error' })
      }
    }
  }

  const handleArchiveConfirm = async () => {
    if (!archiving) return
    try {
      await archive.mutateAsync({ [config.numberKey]: archiving.number })
      setArchiving(null)
      toast({ title: `${cap(config.singular)} archived`, type: 'success' })
    } catch (err) {
      log.error(`Failed to archive ${config.singular}`, err)
      toast({ title: `Could not archive ${config.singular}`, description: (err as Error)?.message || 'Please try again.', type: 'error' }); setArchiving(null)
    }
  }

  const Icon = config.icon

  // ---- Full-page Add/Edit form (no modal) ---------------------------------
  if (view === 'form') {
    return (
      <div className="space-y-4">
        <Button variant="ghost" className="gap-2" onClick={backToList}><ArrowLeft className="h-4 w-4" />Back to {config.title}</Button>
        <Card className="shadow-sm pb-0 overflow-hidden">
          <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><Icon className="h-5 w-5 text-primary" /></div>
              <div>
                <CardTitle className="text-lg">{formMode === 'add' ? `Add ${config.singular}` : `Edit ${editingRow?.name}`}</CardTitle>
                <CardDescription>Enter the {config.singular} details below.</CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-2">
              <Label>Name {formMode === 'add' && <span className="text-destructive">*</span>}</Label>
              <Input value={fName} onChange={(e) => setFName(e.target.value)} disabled={formMode === 'edit'} placeholder={config.namePlaceholder} className={`font-mono ${nameError ? 'border-destructive focus-visible:ring-destructive' : ''}`} />
              <p className="text-xs text-muted-foreground">{config.nameHint} — can't be changed once it's used.</p>
              {nameError && <p className="text-sm text-destructive">{nameError}</p>}
            </div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={fDesc} onChange={(e) => setFDesc(e.target.value)} /></div>
            <div className="space-y-2 max-w-xs"><Label>Status</Label>
              <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent></Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={backToList}>Cancel</Button>
              <Button onClick={handleSave} disabled={busy}>{busy ? 'Saving...' : 'Save'}</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) return <Card className="shadow-sm"><CardContent className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></CardContent></Card>
  if (isError) return <Card className="shadow-sm"><CardContent className="flex flex-col items-center justify-center py-12 gap-4"><p className="text-destructive">Failed to load {config.title.toLowerCase()}</p><Button variant="outline" onClick={() => refetch()}>Try Again</Button></CardContent></Card>

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><Icon className="h-5 w-5 text-primary" /></div>
              <div><CardTitle className="text-xl">{config.title}</CardTitle><CardDescription>Manage event catalog {config.title.toLowerCase()}</CardDescription></div>
            </div>
            {canManage && <Button size="sm" className="gap-2" onClick={openAdd}><Plus className="h-4 w-4" />Add {config.singular}</Button>}
          </div>
        </CardHeader>
      </Card>

      {/* Search + status filter */}
      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder={`Search ${config.title.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <Table className="text-base">
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold text-center">Status</TableHead>
                <TableHead className="w-[120px] text-right font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.length > 0 ? paginated.map((r) => (
                <TableRow key={r.id} className="hover:bg-muted/50">
                  <TableCell className="font-mono font-medium">{r.name}</TableCell>
                  <TableCell className="text-muted-foreground">{r.description || '—'}</TableCell>
                  <TableCell className="text-center"><Badge variant={r.archived ? 'secondary' : r.status === 'active' ? 'default' : 'secondary'}>{r.archived ? 'Archived' : cap(r.status)}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canManage && !r.archived && (<>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setArchiving(r)}><Trash2 className="h-4 w-4" /></Button>
                      </>)}
                    </div>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={4} className="h-24 text-center text-muted-foreground">{search || statusFilter !== 'all' ? `No ${config.title.toLowerCase()} match your filters.` : `No ${config.title.toLowerCase()} yet.`}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t py-3">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Archive confirm (kept as a small confirm dialog, like the app's delete pattern) */}
      <Dialog open={!!archiving} onOpenChange={(o) => !o && setArchiving(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Archive "{archiving?.name}"?</DialogTitle>
            <DialogDescription>
              Archived {config.singular}s can't be picked for new or edited event types.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiving(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleArchiveConfirm} disabled={busy}>Archive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
